const CONSIDERED_EVENTS = new Set([
  "candidate_recorded",
  "cron_driver_added",
]);

const NOTIFY_EVENTS = new Set([
  "push_delivered",
  "push_requested",
  "push_failed",
]);

function mergeDriver(map, uid, patch) {
  if (!uid) {
    return;
  }
  const prev = map.get(uid) || {};
  map.set(uid, { ...prev, ...patch, driverUid: uid });
}

function collectFromBooking(booking, considered) {
  if (!booking) {
    return;
  }
  const estimates = booking.driverEstimates || {};
  const requested = booking.requestedDrivers || {};
  const preRequested = booking.preRequestedDrivers || {};
  const uids = new Set([
    ...Object.keys(estimates),
    ...Object.keys(requested),
    ...Object.keys(preRequested),
  ]);
  uids.forEach((uid) => {
    const est = estimates[uid] || {};
    mergeDriver(considered, uid, {
      distance: est.distance ?? null,
      timein_text: est.timein_text ?? null,
      source: "booking_snapshot",
    });
  });
}

/**
 * @param {Array<object>} logEntries
 * @param {object|null} booking
 * @returns {{ considered: object[], notified: object[] }}
 */
export function summarizeDispatchLog(logEntries, booking) {
  const consideredMap = new Map();
  const notifiedMap = new Map();

  const sorted = [...(logEntries || [])].sort(
    (a, b) => (a.at || 0) - (b.at || 0),
  );

  for (const entry of sorted) {
    const uid = entry.driverUid;
    if (CONSIDERED_EVENTS.has(entry.event) && uid) {
      mergeDriver(consideredMap, uid, {
        distance: entry.distance ?? consideredMap.get(uid)?.distance,
        timein_text: entry.timein_text ?? consideredMap.get(uid)?.timein_text,
        platform: entry.platform ?? consideredMap.get(uid)?.platform,
        source: entry.source,
      });
    }

    if (entry.event === "dispatch_cleared") {
      const uids = [
        ...(entry.requestedDriverUids || []),
        ...(entry.preRequestedDriverUids || []),
      ];
      uids.forEach((u) => {
        mergeDriver(consideredMap, u, { source: "dispatch_cleared" });
      });
    }

    if (NOTIFY_EVENTS.has(entry.event) && uid) {
      let outcome = "sent";
      if (entry.event === "push_delivered") {
        outcome = "delivered";
      } else if (entry.event === "push_failed") {
        outcome = "failed";
      }
      const prev = notifiedMap.get(uid);
      const rank = { delivered: 3, failed: 2, sent: 1 };
      if (!prev || rank[outcome] >= rank[prev.outcome]) {
        mergeDriver(notifiedMap, uid, {
          outcome,
          platform: entry.platform ?? prev?.platform,
          detail: entry.expoMessage || entry.reason || prev?.detail,
        });
      }
    }
  }

  if (consideredMap.size === 0) {
    collectFromBooking(booking, consideredMap);
  }

  return {
    considered: Array.from(consideredMap.values()),
    notified: Array.from(notifiedMap.values()),
  };
}

export function resolveDriverDisplayName(uid, users) {
  if (!uid) {
    return "";
  }
  if (!users || !users.length) {
    return uid;
  }
  const user = users.find(
    (u) => u.id === uid || u.uid === uid || u.key === uid,
  );
  if (!user) {
    return uid;
  }
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name || user.mobile || uid;
}
